/**
 * EnrichmentFields — generic renderer for a kind's enrichment attributes,
 * reusing EntityDetailView's existing enrichment-block/-field/-label/-value
 * classes. Filters out absent/empty fields; each present field optionally
 * carries an InlineHelp glossary affordance next to its label.
 */
import type { JSX } from 'react';
import { InlineHelp } from './InlineHelp.js';

export interface EnrichmentFieldSpec {
  key: string;
  label: string;
  value: string | number | boolean | string[] | undefined;
  glossary?: string;
}

function isPresent(value: EnrichmentFieldSpec['value']): boolean {
  if (value === undefined || value === '') return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
}

export function EnrichmentFields({ fields }: { fields: EnrichmentFieldSpec[] }): JSX.Element | null {
  const present = fields.filter((f) => isPresent(f.value));
  if (present.length === 0) return null;
  return (
    <div className="enrichment-block" data-testid="enrichment-block">
      {present.map((f) => (
        <div className="enrichment-field" key={f.key}>
          <span className="enrichment-label">
            {f.label}
            {f.glossary && <InlineHelp text={f.glossary} />}
          </span>
          {Array.isArray(f.value) ? (
            <ul className="enrichment-list">
              {f.value.map((v, i) => (
                <li key={i}>{v}</li>
              ))}
            </ul>
          ) : (
            <span className="enrichment-value">{String(f.value)}</span>
          )}
        </div>
      ))}
    </div>
  );
}
